/**
 * EquationStudio — drop-in equation editor for the teacher Content Studio.
 *
 * Capabilities:
 *   • Live KaTeX preview with `mhchem` (chemistry) enabled
 *   • Symbol palette (math, Greek, calculus, sets, arrows, physics, chemistry)
 *   • AI Assistant (Claude Sonnet 4.5 via Emergent LLM Key):
 *       - Generate from natural language     → /equation/generate
 *       - Extract from pasted text/transcript → /equation/extract
 *       - Step-by-step Solve                  → /equation/solve
 *       - Explain term-by-term                → /equation/explain
 *       - Chemistry Balance                   → /equation/balance
 *   • "Insert into section" callback hands back rich block (LaTeX + plain + steps)
 *
 * Works as a panel (drawer) or inline. Identical contract for web & mobile —
 * the entire intelligence lives in the shared backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import 'katex/dist/contrib/mhchem.js'; // chemistry extension — adds \ce{} support
import {
  X, Calculator, Sparkles, Wand2, AlertTriangle, Check,
  Atom, BookOpen, ChevronDown, ChevronUp, Copy, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { friendlyError, notifyError, MESSAGES } from '../../lib/errors';
import { PRIMARY_BTN, SECONDARY_BTN } from './teacherUi';
import { InlineSparkle } from '../Loader';

// The shared "selected AI assistant" text points at the chat's model picker,
// which the Equation section doesn't have.
const EQUATION_AI_UNAVAILABLE = 'AI is currently not available in the Equation section. Please try again in a moment.';
const notifyEquationError = (e, fallback) => {
  if (!friendlyError(e, fallback).startsWith(MESSAGES.aiUnavailable)) return notifyError(e, fallback);
  toast.error(EQUATION_AI_UNAVAILABLE, { id: 'error:equation-ai' });
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOFT = '#F0FDF4';
const DARK = '#0C1A12';
const INK = '#1F2937';

function authHeaders() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TABS = [
  { id: 'compose',  label: 'Compose',  icon: Wand2,       sub: 'Type or paste an equation' },
  { id: 'generate', label: 'Generate', icon: Sparkles,    sub: 'Plain words into an equation' },
  { id: 'extract',  label: 'Extract',  icon: BookOpen,    sub: 'Find equations in text' },
  { id: 'balance',  label: 'Balance',  icon: Atom,        sub: 'Balance a chemical reaction' },
];

const PALETTE_FALLBACK = {
  basic: [['+','+'],['−','-'],['×','\\times'],['÷','\\div'],['=','='],['≠','\\ne'],['≈','\\approx'],['≤','\\le'],['≥','\\ge'],['±','\\pm']],
  powers_fractions: [['x²','^{2}'],['x³','^{3}'],['√','\\sqrt{}'],['a/b','\\frac{}{}']],
  calculus: [['∑','\\sum'],['∫','\\int'],['d/dx','\\frac{d}{dx}'],['∂','\\partial'],['lim','\\lim_{ \\to }']],
  greek: [['α','\\alpha'],['β','\\beta'],['γ','\\gamma'],['Δ','\\Delta'],['θ','\\theta'],['λ','\\lambda'],['μ','\\mu'],['π','\\pi'],['σ','\\sigma'],['ω','\\omega']],
  physics: [['Vector','\\vec{}'],['Newton','\\,\\text{N}'],['Joule','\\,\\text{J}'],['m/s²','\\,\\text{m/s}^{2}'],['kg','\\,\\text{kg}']],
  chemistry: [['→ rxn','\\ce{->}'],['⇌','\\ce{<=>}'],['(g)','(g)'],['(l)','(l)'],['(aq)','(aq)'],['H2O','\\ce{H2O}'],['CO2','\\ce{CO2}'],['H+','\\ce{H+}']],
  sets_logic: [['∈','\\in'],['∉','\\notin'],['∪','\\cup'],['∩','\\cap'],['∞','\\infty']],
  arrows: [['→','\\to'],['⇔','\\Leftrightarrow'],['⇒','\\Rightarrow']],
};

function safeLatex(latex) {
  // Strip outer $..$ if present
  if (!latex) return '';
  let s = latex.trim();
  if (s.startsWith('$$') && s.endsWith('$$')) s = s.slice(2, -2);
  if (s.startsWith('$') && s.endsWith('$')) s = s.slice(1, -1);
  return s;
}

function KaTeX({ latex, displayMode = true }) {
  const s = safeLatex(latex);
  if (!s) return null;
  try {
    return displayMode ? <BlockMath math={s}/> : <InlineMath math={s}/>;
  } catch {
    return <code className="text-xs text-red-600">[LaTeX error] {s}</code>;
  }
}

/** Steps to persist with an inserted equation — final answer appended when the model left it out of the steps. */
function stepsWithFinal(result) {
  const steps = result?.steps || [];
  const final = result?.final;
  if (!final?.latex || safeLatex(steps[steps.length - 1]?.latex) === safeLatex(final.latex)) return steps;
  return [...steps, { order: steps.length + 1, description: 'Final answer', rule: '', latex: final.latex }];
}

/** Whole worked solution as one copyable LaTeX block, one step per line. */
function solutionLatex(result) {
  const lines = stepsWithFinal(result).map(s => safeLatex(s.latex)).filter(Boolean);
  return lines.length ? `\\begin{gathered}\n${lines.join(' \\\\\n')}\n\\end{gathered}` : '';
}

export default function EquationStudio({ open, onClose, defaultLatex = '', onInsert, contextText = '' }) {
  const [tab, setTab] = useState('compose');
  const [palette, setPalette] = useState(PALETTE_FALLBACK);
  const [latex, setLatex] = useState(defaultLatex);
  const [busy, setBusy] = useState(false);
  const [displayMode, setDisplayMode] = useState(true);
  // Subject is detected by the backend from the equation itself — never picked by hand.
  const [detectedSubject, setDetectedSubject] = useState(null);
  const taRef = useRef(null);

  // Generate
  const [genText, setGenText] = useState('');
  const [genResult, setGenResult] = useState(null);

  // Extract
  const [extractText, setExtractText] = useState(contextText || '');
  const [extracted, setExtracted] = useState([]);

  // Solve / Explain
  const [solveResult, setSolveResult] = useState(null);
  const [explainResult, setExplainResult] = useState(null);

  // Balance
  const [balText, setBalText] = useState('');
  const [balResult, setBalResult] = useState(null);

  // Load palette
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/teacher/studio/equation/palette`, { headers: authHeaders() });
        if (r.data?.palette) setPalette(r.data.palette);
      } catch { /* keep fallback */ }
    })();
  }, [open]);

  // Reset when toggled
  useEffect(() => {
    if (!open) {
      setSolveResult(null); setExplainResult(null);
    } else if (defaultLatex) {
      setLatex(defaultLatex);
    }
  }, [open, defaultLatex]);

  // Any new LaTeX invalidates results computed for the old one.
  const loadLatex = (next, subject = null) => {
    setLatex(next);
    setDetectedSubject(subject);
    setSolveResult(null);
    setExplainResult(null);
  };

  const insertToken = (token) => {
    const ta = taRef.current;
    if (!ta) return loadLatex(latex + token);
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = latex.slice(0, start) + token + latex.slice(end);
    loadLatex(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + token.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const doGenerate = async () => {
    if (!genText.trim()) return toast.error('Type a description');
    setBusy(true); setGenResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/generate`,
        { description: genText },
        { headers: authHeaders() });
      setGenResult(r.data);
      loadLatex(r.data.latex, r.data.subject || null);
      setTab('compose');
      toast.success(`Generated: ${r.data.title || 'equation'}`);
    } catch (e) {
      notifyEquationError(e, "Couldn't generate an equation from that description. Please try rephrasing.");
    } finally { setBusy(false); }
  };

  const doExtract = async () => {
    if (!extractText.trim()) return toast.error('Paste some text first');
    setBusy(true); setExtracted([]);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/extract`,
        { text: extractText },
        { headers: authHeaders() });
      setExtracted(r.data.equations || []);
      toast.success(`Found ${r.data.equations?.length || 0} equation(s)`);
    } catch (e) {
      notifyEquationError(e, "Couldn't extract equations from that text. Please try again.");
    } finally { setBusy(false); }
  };

  const doSolve = async () => {
    if (!latex.trim()) return toast.error('Enter an equation first');
    setBusy(true); setSolveResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/solve`,
        { latex },
        { headers: authHeaders() });
      setSolveResult(r.data);
      if (r.data.subject) setDetectedSubject(r.data.subject);
    } catch (e) {
      notifyEquationError(e, "Couldn't solve that problem. Please check the LaTeX and try again.");
    } finally { setBusy(false); }
  };

  const doExplain = async () => {
    if (!latex.trim()) return toast.error('Enter an equation first');
    setBusy(true); setExplainResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/explain`,
        { latex },
        { headers: authHeaders() });
      setExplainResult(r.data);
      if (r.data.subject) setDetectedSubject(r.data.subject);
    } catch (e) {
      notifyEquationError(e, "Couldn't explain that equation. Please try again.");
    } finally { setBusy(false); }
  };

  const doBalance = async () => {
    if (!balText.trim()) return toast.error('Enter a reaction');
    setBusy(true); setBalResult(null);
    try {
      const r = await axios.post(`${API}/teacher/studio/equation/balance`,
        { equation: balText }, { headers: authHeaders() });
      setBalResult(r.data);
      loadLatex(r.data.balanced_latex, 'chemistry');
      setTab('compose');
      toast.success('Balanced');
    } catch (e) {
      notifyEquationError(e, "Couldn't balance that reaction. Please check the formula and try again.");
    } finally { setBusy(false); }
  };

  const insertIntoSection = () => {
    if (!onInsert) return;
    onInsert({
      latex,
      subject: detectedSubject,
      title: genResult?.title || balResult?.input || 'Equation',
      plain: genResult?.explanation || explainResult?.concept || null,
      steps: stepsWithFinal(solveResult),
      variables: genResult?.variables || explainResult?.terms || [],
      mode: displayMode ? 'display' : 'inline',
    });
    toast.success('Inserted into section');
    onClose?.();
  };

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed — clipboard unavailable'); }
  };
  const copyLatex = () => copyText(latex, 'LaTeX');
  const copySolution = () => copyText(solutionLatex(solveResult), 'Solution LaTeX');

  if (!open) return null;

  return (
    <div className="fixed inset-0 !m-0 z-[65] flex bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="equation-studio">
      <div className="ml-auto h-full w-full sm:w-[720px] lg:w-[840px] bg-white shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-slate-900">Equation Studio</h2>
            <p className="mt-1 text-sm text-slate-600">
              Write an equation, turn plain words into one, find equations in your notes, or balance a reaction
            </p>
          </div>
          <button onClick={onClose} data-testid="eq-close" aria-label="Close" className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 flex-shrink-0">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 border-b border-slate-200 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} title={t.sub}
              data-testid={`eq-tab-${t.id}`}
              className={`flex items-center gap-2 text-sm font-medium px-3 h-12 border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'text-slate-900 border-[#34A853]' : 'text-slate-500 border-transparent hover:text-slate-800'}`}>
              <t.icon className="w-4 h-4"/>{t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50/60">
          {tab === 'compose' && (
            <ComposePane
              latex={latex} setLatex={loadLatex}
              displayMode={displayMode} setDisplayMode={setDisplayMode}
              taRef={taRef} insertToken={insertToken} palette={palette}
              onSolve={doSolve} onExplain={doExplain} solveResult={solveResult} explainResult={explainResult}
              busy={busy} onCopySolution={copySolution}
            />
          )}
          {tab === 'generate' && (
            <GeneratePane text={genText} setText={setGenText} result={genResult} busy={busy} onRun={doGenerate}/>
          )}
          {tab === 'extract' && (
            <ExtractPane text={extractText} setText={setExtractText} items={extracted} busy={busy} onRun={doExtract}
              onPick={(e) => { loadLatex(e.latex, e.subject || null); setTab('compose'); }}/>
          )}
          {tab === 'balance' && (
            <BalancePane text={balText} setText={setBalText} result={balResult} busy={busy} onRun={doBalance}
              onUse={() => { loadLatex(balResult?.balanced_latex || '', 'chemistry'); setTab('compose'); }}/>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-500 max-w-md truncate min-w-0">
            {latex && <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{latex.slice(0, 80)}{latex.length > 80 ? '…' : ''}</code>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyLatex} data-testid="eq-copy" disabled={!latex}
              className={`${SECONDARY_BTN} inline-flex items-center gap-2`}>
              <Copy className="w-4 h-4"/> Copy LaTeX
            </button>
            {onInsert && (
              <button onClick={insertIntoSection} disabled={!latex}
                data-testid="eq-insert"
                className={`${PRIMARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
                <Plus className="w-4 h-4"/> Insert into section
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Compose pane ────────────────────────────────────────────────────────────
function ComposePane({ latex, setLatex, displayMode, setDisplayMode, taRef, insertToken, palette, onSolve, onExplain, solveResult, explainResult, busy, onCopySolution }) {
  const canRun = !busy && Boolean(latex.trim());
  const [expanded, setExpanded] = useState({ basic: true, powers_fractions: true, greek: true, chemistry: false, calculus: false, physics: false, sets_logic: false, arrows: false });
  return (
    <div className="space-y-4">
      {/* Preview card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-900">Preview</span>
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
            <button onClick={() => setDisplayMode(true)} aria-pressed={displayMode}
                    className={`h-8 px-3 rounded-md text-sm font-medium transition-colors ${displayMode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              Own line
            </button>
            <button onClick={() => setDisplayMode(false)} aria-pressed={!displayMode}
                    className={`h-8 px-3 rounded-md text-sm font-medium transition-colors ${!displayMode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              In text
            </button>
          </div>
        </div>
        <div className="min-h-[60px] flex items-center justify-center bg-slate-50 rounded-lg p-4" data-testid="eq-preview">
          {latex ? <KaTeX latex={latex} displayMode={displayMode}/> : <span className="text-sm text-slate-400">Your equation shows here as you type</span>}
        </div>
      </div>

      {/* Editor */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-sm font-medium text-slate-900">LaTeX source</label>
        <textarea
          ref={taRef}
          value={latex}
          onChange={(e) => setLatex(e.target.value)}
          rows={3}
          data-testid="eq-latex-input"
          placeholder="Type LaTeX or tap a symbol below, e.g. F = m a"
          className="mt-2 w-full font-mono text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]"
        />
        <div className="mt-3 flex gap-2 flex-wrap">
          <button onClick={onSolve} disabled={!canRun} data-testid="eq-solve-btn"
            className={`${PRIMARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
            {busy ? <InlineSparkle size={18} label="Solving" /> : <Calculator className="w-4 h-4"/>}
            Solve step-by-step
          </button>
          <button onClick={onExplain} disabled={!canRun} data-testid="eq-explain-btn"
            className={`${SECONDARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
            <BookOpen className="w-4 h-4 text-slate-500"/> Explain
          </button>
        </div>
      </div>

      {/* Symbol palette */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-900">Symbols</span>
        </div>
        <div className="p-3 space-y-2 max-h-[260px] overflow-y-auto" data-testid="eq-palette">
          {Object.entries(palette).map(([group, items]) => (
            <div key={group} className="rounded-md bg-slate-50/50">
              <button onClick={() => setExpanded(prev => ({ ...prev, [group]: !prev[group] }))}
                className="w-full px-2.5 py-1.5 flex items-center justify-between text-left">
                <span className="text-sm font-medium text-slate-700 capitalize">{group.replace('_', ' ')}</span>
                {expanded[group] ? <ChevronUp className="w-3 h-3 text-slate-400"/> : <ChevronDown className="w-3 h-3 text-slate-400"/>}
              </button>
              {expanded[group] && (
                <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                  {items.map(([label, token], i) => (
                    <button key={`${group}-${i}`} onClick={() => insertToken(token)}
                      data-testid={`eq-sym-${group}-${i}`}
                      title={token}
                      className="min-w-11 h-9 px-2 rounded-md bg-white border border-slate-200 text-sm font-mono text-slate-800 hover:bg-green-50 hover:border-green-300 transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Solve result */}
      {solveResult && (
        <div className="rounded-xl border border-green-200 bg-green-50/40 p-4" data-testid="eq-solve-result">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700">
              <Check className="w-3 h-3"/> Step-by-step solution{solveResult.solving_for ? ` · solving for ${solveResult.solving_for}` : ''}
            </div>
            <button onClick={onCopySolution} data-testid="eq-copy-solution"
              className="h-8 px-3 text-sm font-medium text-slate-900 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg inline-flex items-center gap-1.5 transition-colors">
              <Copy className="w-4 h-4 text-slate-500"/> Copy solution
            </button>
          </div>
          <ol className="space-y-2.5">
            {(solveResult.steps || []).map((s, i) => (
              <li key={i} className="bg-white rounded-lg border border-green-100 p-2.5">
                <div className="text-xs font-semibold text-slate-600 mb-1">Step {s.order}. {s.description}</div>
                <div className="text-xs text-slate-500 mb-1 italic">{s.rule}</div>
                <KaTeX latex={s.latex}/>
              </li>
            ))}
          </ol>
          {solveResult.final && (
            <div className="mt-3 bg-white rounded-lg border-2 border-green-300 p-3">
              <div className="text-xs font-semibold text-green-700 mb-1">Final</div>
              <KaTeX latex={solveResult.final.latex}/>
              {solveResult.final.unit && <div className="text-xs text-slate-500 mt-1">Unit: {solveResult.final.unit}</div>}
            </div>
          )}
          {solveResult.common_mistakes?.length > 0 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <strong className="block mb-1">⚠ Common mistakes:</strong>
              <ul className="list-disc pl-4 space-y-0.5">
                {solveResult.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Explain result */}
      {explainResult && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="eq-explain-result">
          <div className="text-xs font-semibold text-blue-700 mb-2">Explanation · {explainResult.name}</div>
          <p className="text-sm text-slate-700 mb-3">{explainResult.concept}</p>
          {(explainResult.terms || []).length > 0 && (
            <table className="w-full text-xs mt-2">
              <thead><tr className="text-slate-500"><th className="text-left px-2 py-1">Symbol</th><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Meaning</th><th className="text-left px-2 py-1">Unit</th></tr></thead>
              <tbody>
                {explainResult.terms.map((t, i) => (
                  <tr key={i} className="border-t border-blue-100"><td className="px-2 py-1 font-mono">{t.sym}</td><td className="px-2 py-1 font-semibold">{t.name}</td><td className="px-2 py-1">{t.meaning}</td><td className="px-2 py-1">{t.unit}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {explainResult.example && (
            <div className="mt-3 bg-white rounded-lg p-3 border border-blue-100">
              <div className="text-xs font-semibold text-blue-700 mb-1">Example</div>
              <p className="text-xs text-slate-700 mb-2">{explainResult.example.prompt}</p>
              <KaTeX latex={explainResult.example.latex}/>
              {explainResult.example.answer && <p className="text-xs font-semibold text-green-700 mt-1">→ {explainResult.example.answer}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Generate pane ───────────────────────────────────────────────────────────
function GeneratePane({ text, setText, result, busy, onRun }) {
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-slate-900">Describe in plain English</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
        data-testid="eq-gen-text"
        placeholder="e.g. the quadratic formula · the ideal gas law · derivative of sin x · F = ma with units"
        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-gen-btn"
        className={`${PRIMARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
        {busy ? <InlineSparkle size={18} label="Generating equation" /> : <Sparkles className="w-4 h-4"/>}
        Generate equation
      </button>
      {result && (
        <div className="rounded-xl border border-green-200 bg-white p-4 space-y-2" data-testid="eq-gen-result">
          <div className="text-xs font-semibold text-green-700">{result.subject} · {result.kind}</div>
          <div className="text-sm font-semibold text-slate-800">{result.title}</div>
          <div className="bg-slate-50 rounded-lg p-3"><KaTeX latex={result.latex}/></div>
          {(result.alt_forms || []).length > 0 && (
            <div>
              <div className="text-sm font-medium text-slate-900mt-2 mb-1">Alternative forms</div>
              {result.alt_forms.map((f, i) => <div key={i} className="bg-slate-50 rounded-lg p-2 mt-1"><KaTeX latex={f}/></div>)}
            </div>
          )}
          {(result.variables || []).length > 0 && (
            <div className="text-xs">
              <div className="font-semibold text-slate-600 mt-2 mb-1">Variables</div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                {result.variables.map((v, i) => <li key={i} className="text-slate-600"><code className="font-semibold text-slate-800">{v.sym}</code> — {v.name} <span className="text-slate-400">({v.unit})</span></li>)}
              </ul>
            </div>
          )}
          {result.explanation && <p className="text-xs text-slate-600 italic">{result.explanation}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Extract pane ────────────────────────────────────────────────────────────
function ExtractPane({ text, setText, items, busy, onRun, onPick }) {
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-slate-900">Paste transcript / OCR / notes</label>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
        data-testid="eq-extract-text"
        placeholder="Paste a lecture transcript, board OCR, or any text containing math/chemistry…"
        className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-extract-btn"
        className={`${PRIMARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
        {busy ? <InlineSparkle size={18} label="Extracting equations" /> : <BookOpen className="w-4 h-4"/>}
        Extract equations
      </button>
      {items.length > 0 && (
        <ul className="space-y-2" data-testid="eq-extract-list">
          {items.map((e, i) => (
            <li key={e.id || i} className="rounded-xl border border-slate-200 bg-white p-3 hover:border-green-300 transition-colors cursor-pointer"
                onClick={() => onPick?.(e)}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-semibold text-green-700">{e.subject || 'math'} · {e.kind || 'formula'}</span>
                {e.title && <span className="text-xs font-semibold text-slate-700">{e.title}</span>}
              </div>
              <div className="bg-slate-50 rounded p-2"><KaTeX latex={e.latex}/></div>
              {e.context_snippet && <p className="text-xs text-slate-400 mt-1 italic truncate">"{e.context_snippet}"</p>}
              <div className="text-xs text-green-700 mt-2 font-semibold">→ Tap to load into editor</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Balance pane ────────────────────────────────────────────────────────────
function BalancePane({ text, setText, result, busy, onRun, onUse }) {
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-slate-900">Unbalanced chemical equation</label>
      <input value={text} onChange={e => setText(e.target.value)}
        data-testid="eq-balance-input"
        placeholder="e.g. C3H8 + O2 -> CO2 + H2O"
        className="w-full text-sm font-mono rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]"/>
      <button onClick={onRun} disabled={busy || !text.trim()} data-testid="eq-balance-btn"
        className={`${PRIMARY_BTN} inline-flex items-center gap-2 disabled:cursor-not-allowed`}>
        {busy ? <InlineSparkle size={18} label="Balancing reaction" /> : <Atom className="w-4 h-4"/>}
        Balance reaction
      </button>
      {result && (
        <div className="rounded-xl border border-green-200 bg-white p-4" data-testid="eq-balance-result">
          <div className="text-xs font-semibold text-green-700">{result.type || 'reaction'}</div>
          <div className="bg-green-50 border border-green-100 rounded-lg p-3 mt-2"><KaTeX latex={result.balanced_latex}/></div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
            <div>
              <div className="font-semibold text-slate-600 mb-1">Reactants</div>
              {(result.reactants || []).map((r, i) => (
                <div key={i} className="text-slate-700"><code className="font-semibold">{r.formula}</code> <span className="text-slate-400">({r.state})</span> · {r.name}</div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-600 mb-1">Products</div>
              {(result.products || []).map((r, i) => (
                <div key={i} className="text-slate-700"><code className="font-semibold">{r.formula}</code> <span className="text-slate-400">({r.state})</span> · {r.name}</div>
              ))}
            </div>
          </div>
          {result.atom_check && (
            <div className="mt-3 text-xs bg-slate-50 rounded p-2">
              <div className="font-semibold text-slate-600 mb-1">Atom check</div>
              {Object.entries(result.atom_check).map(([atom, line]) => (
                <div key={atom} className="text-slate-600 font-mono"><strong>{atom}:</strong> {line}</div>
              ))}
            </div>
          )}
          <button onClick={onUse} className={`${SECONDARY_BTN} mt-3 inline-flex items-center gap-2`}>
            Use this in Compose
          </button>
        </div>
      )}
    </div>
  );
}
